import { resolveObjectVitality } from '../inventory/object-vitality.js';
import { type JanusCtx, type Row, actorId, ec, ecLean, handBack, insertRoll, itemField, record, refuse, requiredInt, scalar, upper } from './context.js';
import type { JanusInput } from './janus.js';
import type { AbilityUseReaders } from '../actions/ability-use.js';
import { throwObjectVerdict, throwObjectRangeRefusal, THROW_OBJECT_DRAW_AP } from '../actions/throw-object-gate.js';
import { throwObjectMaxSizeId } from '../actions/object-handling-layers.js';
import { THROW_OBJECT_ABILITY_ID, THROW_OBJECT_VARIABLE, throwObjectWeightTier } from '../../shared/throw-object-range.js';
import { floorProjectileRecovery, type WeaponInventoryChange } from '../actions/weapon-attack-inventory.js';
import { sourceItemType } from './charged-cast.js';
import { posCoord } from '../../shared/array-types.js';
import { attackRoll } from './rolls/attack-roll.js';
import { withRollDispatch } from './rolls/janus-dispatch-roll-types.js';
import { selectSkillForContestingRoll, type SelectedSkill } from './select-skill-for-contesting-roll.js';
import { templateAbilitiesGroundAreaCost } from './templates/template-abilities-roll.js';
import { inventoryRestZ } from '../inventory/floor-move.js';
import { arcApexHeightFt } from '../../shared/aoe-arc-trajectory.js';
import { skipUnported, unportedSkipRow } from '../unported.js';

export function isThrowObjectCast(ability: Row, input: Partial<Pick<JanusInput, 'useAbilityVariable1'>>): boolean {
  return upper(itemField(ability, 'item_id')) === THROW_OBJECT_ABILITY_ID && input.useAbilityVariable1 === THROW_OBJECT_VARIABLE;
}

/** Shape only this cast, before either the placed expansion or janus reads its range and dice. */
export function shapeThrowObjectCast(read: AbilityUseReaders, ability: Row, caster: Row, encounterId: string,
  input: Partial<Pick<JanusInput, 'useAbilityVariable1' | 'useAbilityVariable2' | 'throwLob'>>, encounter?: Row): Row {
  if (!isThrowObjectCast(ability, input)) return ability;
  if (!read.resolveInventory || !read.inventoryField || !read.creatureSizeId || !read.itemSizeId)
    handBack('throw-object:readers-missing');
  const row = read.resolveInventory(upper(input.useAbilityVariable2));
  const verdict = throwObjectVerdict({ caster, casterId: actorId(caster), row, encounterId, encounter }, {
    value: read.value, inventoryField: read.inventoryField, itemField,
    creatureSizeId: read.creatureSizeId, itemSizeId: read.itemSizeId,
    throwMaxSizeId: read.resolveItem ? creature => throwObjectMaxSizeId(creature,
      { value: read.value, entries: read.entries, definition: read.resolveItem! }) : undefined,
    telekineticReach: read.telekineticReach,
    resolveInventory: read.resolveInventory,
  });
  if (verdict.refusal) refuse(verdict.refusal);
  const rangeRefusal = throwObjectRangeRefusal(verdict);
  if (rangeRefusal) refuse(rangeRefusal);
  const tier = throwObjectWeightTier(verdict.weightLb);
  return { ...ability, computed: { ...record(ability.computed), range: verdict.rangeFt,
    throw_mode: input.throwLob === 0 ? 'direct' : 'lob',
    throw_object: { size_id: verdict.objectSizeId, inventory_id: upper(input.useAbilityVariable2), weight_lb: verdict.weightLb,
      range_ft: verdict.rangeFt, mode: verdict.mode, two_handed: verdict.twoHanded, dice: tier.dice, die: tier.die } },
    // The browser layer reader has no computed.range bridge; its cast-local slot must expose the same feet.
    item_layer_types: { ...record(ability.item_layer_types), range: verdict.rangeFt, dice: tier.dice, die: tier.die } };
}

/** Decided before equipment or spellcasting skills; the shared skill reader maps Psyche to Mind and Throwable to Body. */
export function throwObjectSkill(ctx: JanusCtx, ability: Row, caster: Row): SelectedSkill {
  const skill = record(record(ability.computed)?.throw_object)?.mode === 'telekinesis'
    ? '58A62523-2588-452B-876B-61D8ACF3CCED' : '1A3033B2-1FA4-485A-9D0A-1CD0BC92C961';
  return selectSkillForContestingRoll(ctx, caster, [skill]);
}

export function throwObjectRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  withRollDispatch(ctx, input, ability, caster, targets, () => {
    templateAbilitiesGroundAreaCost(ctx, input, ability, caster);
    for (const target of targets) attackRoll(ctx, input, ability, caster, [target]);
  });
}

/** Plan before the draw; append once after janus succeeds, even when every attack misses or the area is empty. */
export function planThrowObjectLanding(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row,
  row: Row, targetIds: string[]): () => void {
  const [encounterId, coords] = (input.castingTargets ?? '').split(',')[0]!.split('|');
  const xyz = coords?.split(':').map(Number) ?? [];
  if (upper(encounterId) !== upper(ecLean(caster, 'encounter_id')) || xyz.length !== 3 || !xyz.every(Number.isFinite))
    refuse('Choose where to throw it.');
  const [x, y, centerZ] = xyz as [number, number, number];
  const id = upper(ctx.read.inventoryField?.(row, 'inventory_id'));
  const point = { x, y, z: centerZ };
  const world = ctx.read.throwLandingWorld?.();
  if (world) point.z = inventoryRestZ(world, { id, mounted: false, integrated: false, height: 0,
    footprintAt: () => ({ kind: 'circle', x, y, r: 0, zMin: centerZ, zMax: centerZ }) }, x, y, centerZ, false).z;
  const qty = requiredInt(ctx.read.inventoryField?.(row, 'item_qty') ?? 1, 'throw-object:quantity-not-held');
  const position = (target: Row, key: string): unknown => posCoord(target, key as 'x_pos' | 'y_pos' | 'z_pos');
  const struck = new Set(targetIds.map(upper));
  const recovery = floorProjectileRecovery(row, ctx.encounter, upper(encounterId), point, {
    value: ctx.read.value, entries: (target, key) => key === 'encounter_inventories_arr'
      ? ctx.read.entries(target, key).filter(entry => !struck.has(upper(ctx.read.inventoryField?.(record(entry), 'inventory_id'))))
      : ctx.read.entries(target, key), inventoryField: (target, key) => ctx.read.inventoryField?.(target, key), itemField,
    itemType: target => sourceItemType(ctx.read, target) ?? String(itemField(target, 'item_type') ?? ''), computedValue: () => undefined,
  }, position, ctx.read.uuid, target => ctx.read.statuses(target).map(status => upper(scalar(status.status_item_id))));
  if ('reason' in recovery) handBack(recovery.reason);
  const change: WeaponInventoryChange = { inventory_id: id, before: {
    possess_id: String(ctx.read.inventoryField?.(row, 'possess_id') ?? ''), item_qty: qty,
    inventory_deleted: Number(ctx.read.inventoryField?.(row, 'inventory_deleted') ?? 0) },
    after: { item_qty: qty - 1, inventory_deleted: qty === 1 ? 1 : 0 }, recovery, log_type: 'throw_inventory' };
  const thrown = record(record(ability.computed)?.throw_object);
  const origin = thrown?.mode === 'telekinesis' ? row : caster;
  const distance = Math.hypot(x - Number(position(origin, 'x_pos') ?? 0), y - Number(position(origin, 'y_pos') ?? 0));
  const apex = record(ability.computed)?.throw_mode === 'direct' ? 0 : arcApexHeightFt(distance, null);
  return () => {
    (ctx.weaponInventoryChanges ??= []).push(change);
    insertRoll(ctx, { roll_type_1: 'throw_inventory', roll_item_id: id, by: caster, to: ctx.throwObjectFirstHit });
    if (thrown?.mode === 'draw' && ctx.read.inCombat(ctx.encounter, caster))
      ec(caster).aps = requiredInt(ecLean(caster, 'aps'), 'throw-object:aps-not-held') - THROW_OBJECT_DRAW_AP;
    const vitality = resolveObjectVitality(row, ctx.encounter, ctx.read.definition);
    if (apex > 5 && Number(vitality?.v_max ?? 0) > 0)
      ctx.rows.push(ctx.dryRun ? unportedSkipRow('throw-object:impact-fall-damage')
        : skipUnported('throw-object:impact-fall-damage', { inventoryId: id, apexFt: apex, distanceFt: distance }));
  };
}
